import { useEffect } from "react";

// Full-screen chats on phones.
//
// Oct 17, Felipe's iPhone: after typing, the page stayed shifted up.
// Oct 18, again: with the keyboard open the text box sat at the TOP of the
// screen, an empty band between it and the keyboard, and the site's bottom
// bar floating mid-screen. iOS scrolls the page to show the text box before
// the keyboard finishes opening, and only then shrinks what you can see, so
// a chat box sized in the page's flow ends up above the visible area.
//
// So a chat screen follows what you can see (the visual viewport), like a
// native chat app (Felipe: "keep the chat close to the keyboard", the Claude
// app):
//   - --chat-vv-top / --chat-visible-height: where the visible area is and
//     how tall; `.chat-screen` (index.css) is fixed to exactly that, so the
//     header is at the top of the screen and the text box sits on the
//     keyboard, however the browser scrolled;
//   - html[data-chat-keyboard="open"] while the person types: the chat covers
//     the site header too (the Claude app look), and the bottom bar hides;
//   - when the keyboard closes the page goes back to the top.
// The Messages page and the concierge's full-screen overlay use it.

/** How much shorter than the window the visible area must be to mean "keyboard open". */
export const KEYBOARD_GAP_PX = 150;

/** The on-screen keyboard (or other OS chrome) covers the bottom of the window. */
export const keyboardOpen = (innerHeight: number, visualHeight: number): boolean =>
  innerHeight - visualHeight > KEYBOARD_GAP_PX;

export const isTextField = (el: Element | null): boolean => {
  if (!el) return false;
  if (el.tagName === "TEXTAREA" || (el as HTMLElement).isContentEditable) return true;
  if (el.tagName !== "INPUT") return false;
  const type = (el as HTMLInputElement).type;
  return !["button", "submit", "reset", "checkbox", "radio", "range", "color", "file"].includes(type);
};

/** The field belongs to the chat screen itself, not to a dialog over it. */
export const inChatScreen = (el: Element | null): boolean => !!el?.closest?.(".chat-screen");

/**
 * Typing in the chat: one of its text fields has focus, or the visible area
 * says the keyboard is up while no other field has it. A field in a dialog
 * over the chat (Oct 19: the decline reason) is not the chat typing; the
 * chat must not rise over that dialog.
 */
export const isTyping = (): boolean => {
  const active = typeof document !== "undefined" ? document.activeElement : null;
  if (isTextField(active)) return inChatScreen(active);
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  return !!vv && keyboardOpen(window.innerHeight, vv.height);
};

export function useChatViewport(enabled = true): void {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const root = document.documentElement;
    const vv = window.visualViewport;
    let wasTyping = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let frame = 0;

    const restore = () => {
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };
    const apply = () => {
      frame = 0;
      const visible = vv ? vv.height : window.innerHeight;
      root.style.setProperty("--chat-visible-height", `${Math.round(visible)}px`);
      root.style.setProperty("--chat-vv-top", `${Math.round(vv ? Math.max(0, vv.offsetTop) : 0)}px`);
      const typing = isTyping();
      if (typing) root.dataset.chatKeyboard = "open";
      else delete root.dataset.chatKeyboard;
      if (wasTyping && !typing) restore();
      wasTyping = typing;
    };
    // The visible area moves during the keyboard animation: follow it once
    // per frame.
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };
    const onFocusIn = (e: FocusEvent) => {
      if (isTextField(e.target as Element | null)) schedule();
    };
    // Browsers without a visual viewport, and iOS when the keyboard closes
    // without resizing anything: put the page back once focus has left the
    // text fields (not when it just moves to another one).
    const onFocusOut = (e: FocusEvent) => {
      if (!isTextField(e.target as Element | null)) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!isTextField(document.activeElement)) {
          apply();
          restore();
        }
      }, 100);
    };

    apply();
    vv?.addEventListener("resize", schedule);
    vv?.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      clearTimeout(timer);
      if (frame) cancelAnimationFrame(frame);
      vv?.removeEventListener("resize", schedule);
      vv?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      root.style.removeProperty("--chat-visible-height");
      root.style.removeProperty("--chat-vv-top");
      delete root.dataset.chatKeyboard;
    };
  }, [enabled]);
}
